import { useMemo } from 'react';
import { QueryClient, useQuery } from 'react-query';

import { GqlRequestOptions } from '@common/api-request';

import { getPdpSizeComparablePurchasedProductList } from '../graphql/size-info.2';

export const MY_PURCHASE_SIZE_LIST_QUERY_KEY = 'getPdpSizeComparablePurchasedProductList';

export const prefetchMyPurchaseSizeList = async (
  queryClient: QueryClient,
  sizeCategoryId: string,
  options?: GqlRequestOptions,
) => {
  return queryClient.prefetchQuery([MY_PURCHASE_SIZE_LIST_QUERY_KEY, sizeCategoryId], async () => {
    const { data } = await getPdpSizeComparablePurchasedProductList({ size_category_id: sizeCategoryId }, options);
    return data.pdp_size_comparable_purchased_product_list;
  });
};

export interface MyPurchaseItem {
  id: string;
  /** 고유 식별자 (catalog_product_id-size_option_name) - 웹뷰 간 통신에서도 일관된 식별자 */
  uniqueKey: string;
  productName: string;
  brandName: string;
  imageUrl: string;
  /** 화면 노출 용 옵션명 - option_item_name 매핑 */
  optionItemName?: string | null;
  /** 비교하기 mutation 호출 시 사용할 필드 - size_option_name 매핑 */
  sizeOptionName?: string | null;
  option: string;
  reviewStatus: string;
  sizeInfo: Record<string, string | number>;
  isSelected: boolean;
  catalog_product_id: string;
}

export const useMyPurchaseSizeList = (sizeCategoryId?: string) => {
  const {
    data: realData,
    isLoading,
    isError,
  } = useQuery(
    [MY_PURCHASE_SIZE_LIST_QUERY_KEY, sizeCategoryId],
    async () => {
      if (!sizeCategoryId) return null;
      const { data } = await getPdpSizeComparablePurchasedProductList({ size_category_id: sizeCategoryId });
      return data.pdp_size_comparable_purchased_product_list;
    },
    {
      enabled: !!sizeCategoryId,
    },
  );

  const data = useMemo(() => {
    if (realData) {
      return realData
        .map((item, index) => ({
          id: item.catalog_product_id || `real-id-${index}`,
          // catalog_product_id + size_option_name 조합으로 일관된 식별자 생성
          uniqueKey: `${item.catalog_product_id || 'unknown'}-${item.size_option_name || 'unknown'}`,
          productName: item.title,
          brandName: item.shop_name,
          imageUrl: item.image_url,
          optionItemName: item.option_item_name ?? undefined,
          sizeOptionName: item.size_option_name ?? undefined,
          option: '',
          reviewStatus: item.review_comment || '',
          sizeInfo: Object.fromEntries(item.size_value_list),
          isSelected: item.is_comparing,
          catalog_product_id: item.catalog_product_id || '',
        }))
        .sort((a, b) => {
          if (a.isSelected === b.isSelected) return 0;
          return a.isSelected ? -1 : 1;
        });
    }

    return [];
  }, [realData]);

  return {
    data,
    isLoading,
    isError,
  };
};
